/* ---------------------------------------------------------------------------
   Shared game rules. Pure functions, no Convex imports, no DOM imports.
   Imported by BOTH the Convex backend and the React client — this file is the
   single source of truth for every rule. Do not duplicate these numbers.
   --------------------------------------------------------------------------- */

// --- Room shape -------------------------------------------------------------

export const MIN_PLAYERS = 3;
export const MAX_PLAYERS = 8;

/** Host picks one. Length in seconds of the segment a player dubs. */
export const SEGMENT_TIERS = [15, 20, 30] as const;
export type SegmentLength = (typeof SEGMENT_TIERS)[number];

/** Host picks one. Number of rounds in a game. */
export const ROUND_TIERS = [3, 5, 7] as const;
export type RoundCount = (typeof ROUND_TIERS)[number];

/** Fixed. A segment is recorded as a sequence of sub-chunks this long. */
export const SUBCHUNK_SEC = 5;

/** Visual countdown before recording a sub-chunk. No spoken numbers. */
export const COUNTDOWN_SEC = 3;

/** One redo per segment, spent on a single sub-chunk of the player's choice. */
export const REDOS_PER_SEGMENT = 1;

// --- Clip limits ------------------------------------------------------------

export const MAX_CLIP_BYTES = 180 * 1024 * 1024; // 180 MB
export const MAX_CLIP_SEC = 180; // 3 minutes
export const MIN_CLIP_SEC = 5;

/**
 * Containers/codecs every target browser can actually decode.
 * MKV, AVI and HEVC are rejected on purpose: they fail silently mid-game
 * otherwise, and a loud upload-time error is far kinder.
 */
export const ACCEPTED_MIME = [
  "video/mp4", // H.264 + AAC
  "video/webm", // VP8/VP9 + Opus
  "video/quicktime", // .mov, usually H.264 + AAC
] as const;

export const ACCEPTED_EXTENSIONS = [".mp4", ".m4v", ".webm", ".mov"] as const;

// --- Voting -----------------------------------------------------------------

/** Points awarded for 1st / 2nd / 3rd choice. Everything unranked scores 0. */
export const VOTE_POINTS = [3, 2, 1] as const;

/**
 * How many ranked votes a player casts.
 * You cannot vote for your own dub, so with N players there are only N-1
 * rankable dubs. At 3 players that is 2 — asking for 3 votes is impossible.
 */
export function votesAllowed(playerCount: number): number {
  return Math.max(1, Math.min(VOTE_POINTS.length, playerCount - 1));
}

/** Points for a given 0-indexed rank. Out-of-range ranks score nothing. */
export function pointsForRank(rank: number): number {
  return VOTE_POINTS[rank] ?? 0;
}

// --- Sub-chunk maths --------------------------------------------------------

export interface SubChunk {
  index: number;
  /** Seconds, absolute within the source clip. */
  start: number;
  end: number;
}

/**
 * Split a segment into fixed-length sub-chunks.
 * A trailing remainder shorter than half a sub-chunk is merged into the
 * previous one rather than left as a stub nobody can perform.
 */
export function splitSegment(
  segmentStart: number,
  segmentLength: number,
  subChunkSec: number = SUBCHUNK_SEC,
): SubChunk[] {
  const chunks: SubChunk[] = [];
  const end = segmentStart + segmentLength;

  let cursor = segmentStart;
  let index = 0;
  while (cursor < end - 1e-6) {
    const stop = Math.min(cursor + subChunkSec, end);
    chunks.push({ index, start: cursor, end: stop });
    cursor = stop;
    index += 1;
  }

  if (chunks.length > 1) {
    const last = chunks[chunks.length - 1];
    if (last.end - last.start < subChunkSec / 2) {
      chunks.pop();
      chunks[chunks.length - 1].end = last.end;
    }
  }
  return chunks;
}

export function subChunkCount(segmentLength: number): number {
  return splitSegment(0, segmentLength).length;
}

// --- Clip dealing -----------------------------------------------------------

export interface DealableClip {
  clipId: string;
  ownerId: string;
}

export interface DealInput {
  playerIds: string[];
  clips: DealableClip[];
  /** clipIds each player has already dubbed in earlier rounds. */
  alreadyDubbed: Record<string, string[]>;
  /**
   * Solo test rooms only. Lifts the never-your-own-clip rule, which is
   * unsatisfiable when the room has one player and therefore one clip.
   * Never set this for a real game — dubbing your own clip is the one thing
   * the deal exists to prevent.
   */
  allowOwnClip?: boolean;
}

export type DealResult = Record<string, string>; // playerId -> clipId

/**
 * Deal one clip to each player for a round.
 *
 * Hard constraint:  never your own clip.
 * Soft constraint:  prefer a clip you have not dubbed before.
 * Also soft:        prefer not reusing the same clip twice in one round.
 *
 * Exhaustive backtracking. Player counts are capped at MAX_PLAYERS (8), so the
 * search space is trivial and this always finds a solution when one exists.
 * Returns null only when every clip belongs to the one player being dealt to.
 */
export function dealClips(input: DealInput): DealResult | null {
  const { playerIds, clips, alreadyDubbed, allowOwnClip = false } = input;
  if (playerIds.length === 0 || clips.length === 0) return null;

  const seen = (pid: string) => new Set(alreadyDubbed[pid] ?? []);

  // Rank candidates per player: unseen clips first, then seen ones.
  const candidates = new Map<string, string[]>();
  for (const pid of playerIds) {
    const mine = seen(pid);
    const eligible = allowOwnClip ? clips : clips.filter((c) => c.ownerId !== pid);
    if (eligible.length === 0) return null;

    const unseen = eligible.filter((c) => !mine.has(c.clipId));
    const rest = eligible.filter((c) => mine.has(c.clipId));
    candidates.set(pid, [...unseen, ...rest].map((c) => c.clipId));
  }

  // Most-constrained player first — prunes the search hard.
  const order = [...playerIds].sort(
    (a, b) => candidates.get(a)!.length - candidates.get(b)!.length,
  );

  const assignment: DealResult = {};
  const usedThisRound = new Set<string>();

  const solve = (i: number, allowReuse: boolean): boolean => {
    if (i === order.length) return true;
    const pid = order[i];
    for (const clipId of candidates.get(pid)!) {
      if (!allowReuse && usedThisRound.has(clipId)) continue;
      assignment[pid] = clipId;
      usedThisRound.add(clipId);
      if (solve(i + 1, allowReuse)) return true;
      usedThisRound.delete(clipId);
      delete assignment[pid];
    }
    return false;
  };

  // Try for distinct clips first; fall back to allowing reuse when the pool
  // is smaller than the player count.
  if (solve(0, false)) return assignment;

  usedThisRound.clear();
  for (const k of Object.keys(assignment)) delete assignment[k];
  if (solve(0, true)) return assignment;

  return null;
}

// --- Room codes -------------------------------------------------------------

/** No vowels (no accidental words), no 0/O/1/I/L (no misreads over voice). */
const CODE_ALPHABET = "BCDFGHJKMNPQRSTVWXYZ23456789";
export const ROOM_CODE_LENGTH = 4;

export function generateRoomCode(rand: () => number = Math.random): string {
  let out = "";
  for (let i = 0; i < ROOM_CODE_LENGTH; i++) {
    out += CODE_ALPHABET[Math.floor(rand() * CODE_ALPHABET.length)];
  }
  return out;
}

export function normalizeRoomCode(raw: string): string {
  return raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, ROOM_CODE_LENGTH);
}

// --- Phases -----------------------------------------------------------------

export const PHASES = [
  "lobby", // joining, uploading clips, host configures
  "dealing", // clips scrambled and handed out
  "recording", // everyone dubs their assigned segment simultaneously
  "playback", // original plays (skippable), then dubs in random order
  "voting", // ranked votes cast
  "scoreboard", // round results
  "podium", // final standings
] as const;
export type Phase = (typeof PHASES)[number];

// --- Skip-the-original vote -------------------------------------------------

/** Original playback is skipped once a strict majority of players ask for it. */
export function skipThreshold(playerCount: number): number {
  return Math.floor(playerCount / 2) + 1;
}
